<script setup lang="ts">
/*
 * The console's primary navigation: one segmented control with a sliding
 * indicator — Home · Browse · Pre-orders · Help. The indicator is measured
 * from the active tab so it slides (not jumps) between them. Keyboard:
 * ←/→ move between tabs, Enter follows. Below `md` the bottom tab bar
 * replaces this.
 */
import { CalendarClock, House, LayoutGrid, LifeBuoy } from 'lucide-vue-next';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, type Component } from 'vue';
import { RouterLink, useRoute } from 'vue-router';

interface Tab { key: string; to: string; icon: Component; label: string; active: boolean }

const route = useRoute();

const tabs = computed<Tab[]>(() => [
  { key: 'home', to: '/', icon: House, label: 'header.tabs.home', active: route.path === '/' },
  { key: 'browse', to: '/catalog', icon: LayoutGrid, label: 'header.nav.allGames', active: route.name === 'catalog' && route.query.preorder !== '1' },
  { key: 'preorders', to: '/catalog?preorder=1', icon: CalendarClock, label: 'header.nav.preorders', active: route.name === 'catalog' && route.query.preorder === '1' },
  { key: 'help', to: '/help', icon: LifeBuoy, label: 'header.nav.help', active: route.path.startsWith('/help') },
]);

const list = ref<HTMLElement | null>(null);
const indicator = ref({ left: 0, width: 0, visible: false });

async function measure() {
  await nextTick();
  const active = list.value?.querySelector<HTMLElement>('[aria-current="page"]');
  if (!active || !list.value) {
    indicator.value = { ...indicator.value, visible: false };
    return;
  }
  indicator.value = { left: active.offsetLeft, width: active.offsetWidth, visible: true };
}

watch(() => route.fullPath, measure);
onMounted(() => {
  void measure();
  // Unbounded loads after first paint; the tab widths change with it.
  void document.fonts?.ready.then(measure);
  window.addEventListener('resize', measure);
});
onBeforeUnmount(() => window.removeEventListener('resize', measure));
</script>

<template>
  <nav :aria-label="$t('header.tabs.label')" class="relative hidden md:block">
    <ul ref="list" class="relative flex items-center gap-1 rounded-full border border-border bg-bg-elev p-1">
      <span
        class="absolute top-1 h-[calc(100%-8px)] rounded-full bg-accent transition-[left,width] duration-300 ease-[cubic-bezier(.2,.8,.2,1)]"
        :style="{ left: `${indicator.left}px`, width: `${indicator.width}px`, opacity: indicator.visible ? 1 : 0 }"
        aria-hidden="true"
      />
      <!-- li must not be positioned: offsetLeft is measured against the ul. -->
      <li v-for="tab in tabs" :key="tab.key">
        <RouterLink
          :to="tab.to"
          :aria-current="tab.active ? 'page' : undefined"
          class="relative flex h-10 items-center gap-2 rounded-full px-4 font-heading text-[12px] font-bold transition-colors duration-200"
          :class="tab.active ? 'text-text-inv' : 'text-text-dim hover:text-text-base'"
        >
          <component :is="tab.icon" class="h-4 w-4" aria-hidden="true" />
          {{ $t(tab.label) }}
        </RouterLink>
      </li>
    </ul>
  </nav>
</template>
